// Pick a patient by name, using Instance B's search.
//
// Replaces a box that asked a biller to type an internal Atlas id — a number
// nothing in the product shows them. This consumes B's `searchPatients` rather
// than adding a second search endpoint, so there is one definition of what a
// patient search matches (name, DOB, member id) and one place it is scoped to
// the practice.
//
// DEGRADES TO THE NUMERIC FIELD. B's route is gated on `features.patients`, so
// a practice with authorizations on and patients off gets a 404 here. That is a
// real configuration, not a corner, and an authorizations form that cannot name
// a patient at all would be worse than an ugly one — so a failed search falls
// back to the id box with a line saying why.

import React, { useCallback, useEffect, useRef, useState } from "react";

import { searchPatients } from "../patients/api";

export default function PatientPicker({
  practiceId,
  value,
  displayName,
  onChange,
}) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [chosen, setChosen] = useState(
    value
      ? { id: value, display_name: displayName || `Patient ${value}` }
      : null,
  );
  // Keyed to the request, not the effect: a slow search must not overwrite a
  // later one's results just because it resolved second.
  const latest = useRef(0);

  useEffect(() => {
    setChosen(
      value
        ? { id: value, display_name: displayName || `Patient ${value}` }
        : null,
    );
  }, [value, displayName]);

  const run = useCallback(
    async (q) => {
      if (!practiceId) return;
      const seq = ++latest.current;
      setSearching(true);
      try {
        const data = await searchPatients(practiceId, q, 20);
        if (seq !== latest.current) return;
        setResults((data && data.patients) || []);
        setUnavailable(false);
        setSearchFailed(false);
      } catch (err) {
        if (seq !== latest.current) return;
        setResults([]);
        // Only 404 and 403 mean "this practice/user does not get patient
        // search", which is the permanent condition the id fallback exists for.
        // Latching on ANY error meant one transient 500 while typing dropped the
        // biller into "type the Atlas patient id" for the life of the modal —
        // with the wrong explanation.
        if (err && (err.status === 404 || err.status === 403)) {
          setUnavailable(true);
        } else {
          // FAILED is not EMPTY. Falling through to "No patient matches that."
          // tells a biller that a patient who exists does not — and they then
          // file the authorization with no patient link, which leaves that
          // patient's missing-auth alert firing. Same rule AlertsStrip and
          // PatientAuths already follow; this was the one place it was not.
          setSearchFailed(true);
        }
      } finally {
        if (seq === latest.current) setSearching(false);
      }
    },
    [practiceId],
  );

  // Debounced: a search per keystroke is a request per keystroke against a
  // table of patients, and B's route audits every disclosure it makes.
  useEffect(() => {
    if (chosen || unavailable) return undefined;
    const text = term.trim();
    if (text.length < 2) {
      setResults([]);
      return undefined;
    }
    setSearchFailed(false);
    const timer = setTimeout(() => run(text), 250);
    return () => clearTimeout(timer);
  }, [term, chosen, unavailable, run]);

  if (unavailable) {
    return (
      <>
        <label className="auth-field">
          <span>Patient id</span>
          <input
            className="auth-input"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value, null)}
            inputMode="numeric"
            placeholder="Atlas patient id"
          />
        </label>
        <p className="auth-field-hint auth-field-hint-warn auth-field-full">
          Patient search is unavailable for this practice, so this asks for the
          Atlas patient id instead.
        </p>
      </>
    );
  }

  if (chosen) {
    return (
      <label className="auth-field auth-field-wide">
        <span>Patient</span>
        <div className="auth-picked">
          <span className="auth-picked-name">{chosen.display_name}</span>
          <button
            type="button"
            className="auth-btn auth-btn-link"
            onClick={() => {
              setChosen(null);
              setTerm("");
              onChange("", null);
            }}
          >
            change
          </button>
        </div>
      </label>
    );
  }

  return (
    <label className="auth-field auth-field-wide auth-picker">
      <span>Patient</span>
      <input
        className="auth-input"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Search by name, date of birth or member id"
        autoComplete="off"
      />
      {term.trim().length >= 2 && (
        <ul className="auth-picker-results">
          {searching && <li className="auth-picker-empty">Searching…</li>}
          {!searching && searchFailed && (
            <li className="auth-picker-empty auth-picker-failed">
              Patient search failed — this is not "no such patient". Try again.
            </li>
          )}
          {!searching && !searchFailed && results.length === 0 && (
            <li className="auth-picker-empty">No patient matches that.</li>
          )}
          {results.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="auth-picker-hit"
                onClick={() => {
                  setChosen(p);
                  onChange(String(p.id), p.display_name);
                }}
              >
                <span className="auth-picker-hit-name">{p.display_name}</span>
                {(p.patient_dob || p.subscriber_dob) && (
                  <span className="auth-picker-hit-dob">
                    {p.patient_dob || p.subscriber_dob}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </label>
  );
}
